import { Checkbox, Dropdown, Modal, Tooltip } from 'antd'
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import DOMPurify from 'dompurify'
import noteIcon from 'assets/images/calendar/note-icon.svg'
import reminderIcon from 'assets/images/calendar/reminder-icon.svg'
import closeIcon from 'assets/images/toolbar-close.svg'
import deleteIcon from 'assets/images/toolbar-delete.svg'
import editIcon from 'assets/images/toolbar-edit.svg'
import NoteDetail from 'pages/calendar/components/NoteDetail'
import { getSortedFolders } from 'pages/todo/modules/TodoFolderSync'
import { TodoFolder } from '../../modules/TodoFolder'
import { TaskReminder, TODO_STATUS, TodoItem } from '../../modules/TodoItem'
import { formatCompletedDate, formatDueDate, getReminderTimeStr } from '../DateFormatter'

import duplicateIcon from 'assets/images/toolbar-duplicate-24.svg'
import SVG from 'react-inlinesvg'

import taskIcon from 'assets/images/todo/property-tasks.svg'
import Avatar from 'pages/calendar/components/Avatar'
import { getUserInfo } from 'pages/calendar/modules/CalendarService'
import { updateTaskStatus } from '../../modules/TodoManager'
import { getCurrentRecurrenceDescription } from '../../utils/recurrenceUtils'
import styles from './style.module.scss'

interface TodoDetailsProps {
  todo: TodoItem
  onEdit: (todo: TodoItem) => void
  onDuplicate: (todo: TodoItem) => void
  onTodoDelete: (todo: TodoItem) => void
  onTodoUpdate?: ((todo: TodoItem) => void) | null
  onClose: () => void
  onCancel?: () => void
}

const TodoDetails: React.FC<TodoDetailsProps> = ({
  todo,
  onEdit,
  onDuplicate,
  onTodoDelete,
  onTodoUpdate: _onTodoUpdate,
  onClose,
}) => {
  const { t } = useTranslation()
  const [currentTodo, setCurrentTodo] = useState<TodoItem>(todo)
  const [showNoteDetail, setShowNoteDetail] = useState(false)
  const [isViewMore, setIsViewMore] = useState(false)
  const noteRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setCurrentTodo(todo)
  }, [todo])

  const onCompleteToggleTodo = (isCompleted: boolean) => {
    console.log('completeToggleTodo:', currentTodo.summary, isCompleted)

    updateTaskStatus(currentTodo, isCompleted, (success: boolean, updatedTodo?: TodoItem) => {
      if (success) {
        console.log('Task status updated successfully:', currentTodo.summary)

        // updatedTodo는 folder-specific reload 후 fresh etag를 포함
        if (updatedTodo) {
          console.log('✅ Updating TodoDetails with fresh todo (etag:', updatedTodo.etag, ')')
          setCurrentTodo(updatedTodo)
        } else {
          // Fallback: 서버 응답이 없으면 optimistic update
          console.warn('⚠️ No updated todo from server, using optimistic update')
          setCurrentTodo((prev) => ({
            ...prev,
            status: isCompleted ? 'COMPLETED' : 'NEEDS-ACTION',
            percentComplete: isCompleted ? 100 : 0,
            completedDate: isCompleted ? new Date() : null,
          }))
        }
      } else {
        console.log('Task status update failed:', currentTodo.summary)
      }
    })
  }

  const handleDelete = async (deleteType: 'normal' | 'this' | 'all' = 'normal') => {
    const { deleteTodoItem, deleteRecurrenceTodoItem } = await import('../../utils/todoDeleteUtils')
    const { confirmDelete } = await import('utils/ui')

    if (currentTodo.recurrenceRule && deleteType !== 'normal') {
      // 반복 항목 삭제 - 직접 confirmDelete 사용
      const isAllTasks = deleteType === 'all'
      const title = t('todo.editor.deleteTitle')
      const content = isAllTasks
        ? t('todo.editor.deleteAllFutureOccurrences')
        : t('todo.editor.deleteOnlyThisOccurrence')

      confirmDelete({
        title,
        content,
        okText: t('common.button.delete'),
        cancelText: t('common.button.cancel'),
        onOk: async () => {
          try {
            await deleteRecurrenceTodoItem(currentTodo, isAllTasks, t)
            console.log('Recurrence delete success:', currentTodo.summary)
            onTodoDelete(currentTodo)
            if (onClose) onClose()
          } catch (error) {
            console.error('Recurrence delete failed:', error)
          }
        },
        onCancel: () => {
          console.log('Recurrence delete cancelled')
        },
      })
    } else {
      // 일반 항목 삭제 - 직접 confirmDelete 사용
      confirmDelete({
        title: t('todo.editor.deleteTitle'),
        content: t('todo.editor.deleteContent'),
        okText: t('common.button.delete'),
        cancelText: t('common.button.cancel'),
        onOk: async () => {
          try {
            await deleteTodoItem(currentTodo, t)
            console.log('Delete success:', currentTodo.summary)
            onTodoDelete(currentTodo)
            if (onClose) onClose()
          } catch (error) {
            console.error('Delete failed:', error)
          }
        },
        onCancel: () => {
          console.log('Delete cancelled')
        },
      })
    }
  }

  const getFolder = (): TodoFolder | undefined => {
    const sortedFolders = getSortedFolders()
    return sortedFolders.find((f) => f.url === currentTodo.folderUrl)
  }

  if (!currentTodo) return null

  const userInfo = getUserInfo()
  const isMyTodo =
    !currentTodo.author ||
    !currentTodo.author?.val ||
    userInfo.email.toLowerCase() === currentTodo.author?.val.toLowerCase()

  const handleDuplicateEvent = () => {
    onDuplicate(currentTodo)
  }

  const renderToolbar = () => {
    const folder = getFolder()

    // 반복 항목 삭제 메뉴 items
    const deleteMenuItems = [
      {
        key: 'this',
        label: t('todo.editor.deleteOnlyThisOccurrence'),
        onClick: () => handleDelete('this'),
      },
      {
        key: 'all',
        label: t('todo.editor.deleteAllFutureOccurrences'),
        onClick: () => handleDelete('all'),
      },
    ]

    return (
      <div className={styles.toolbar}>
        <div className={styles.leftIcons}>
          {!isMyTodo && (
            <Tooltip title={currentTodo.author?.params?.CN || currentTodo.author?.val}>
              <div className={styles.authorIcon}>
                <Avatar
                  name={currentTodo.author?.params?.CN || currentTodo.author?.val}
                  email={currentTodo.author?.val}
                />
              </div>
            </Tooltip>
          )}
        </div>
        <div className={styles.toolbarButtons}>
          <Tooltip title={t('signature.menu.duplicate')}>
            <button className={styles.duplicateButton} onClick={handleDuplicateEvent}>
              <img src={duplicateIcon} alt="Duplicate" />
            </button>
          </Tooltip>

          {!folder?.isReadOnly && (
            <Tooltip title={t('common.button.edit')}>
              <button
                className={styles.editButton}
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(currentTodo)
                }}
              >
                <img src={editIcon} alt="Edit" />
              </button>
            </Tooltip>
          )}
          {!folder?.isReadOnly && (
            <Tooltip title={t('common.button.delete')}>
              {currentTodo.recurrenceRule ? (
                <Dropdown
                  menu={{ items: deleteMenuItems }}
                  trigger={['click']}
                  overlayClassName={classNames('contextMenu')}
                >
                  <button className={styles.deleteButton}>
                    <img src={deleteIcon} alt="Delete" />
                  </button>
                </Dropdown>
              ) : (
                <button
                  className={styles.deleteButton}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDelete('normal')
                  }}
                >
                  <img src={deleteIcon} alt="Delete" />
                </button>
              )}
            </Tooltip>
          )}
          <Tooltip title={t('common.button.close')}>
            <button
              className={styles.closeButton}
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
            >
              <img src={closeIcon} alt="Close" />
            </button>
          </Tooltip>
        </div>
      </div>
    )
  }

  const renderTitle = () => {
    const subject = currentTodo.summary || t('todo.detail.noSubject')
    const folder = getFolder()
    const folderColor = folder?.calendarColor?._cdata || '#fd4c45'

    return (
      <div className={styles.header}>
        <div className={styles.checkBoxArea}>
          <Checkbox
            checked={currentTodo.status === TODO_STATUS.COMPLETED}
            onChange={(e) => onCompleteToggleTodo(e.target.checked)}
            className={styles.checkBox}
            disabled={folder?.isReadOnly}
            style={{ '--checkbox-color': folderColor } as React.CSSProperties}
          />
        </div>

        <div className={styles.title}>
          <h2>{subject}</h2>
        </div>
      </div>
    )
  }

  const renderTime = () => {
    const dueDateStr = currentTodo.dueDate ? formatDueDate(currentTodo.dueDate, t) : ''
    // Note: rruleDescription is not imported, removing for now
    const rruleStr = currentTodo.recurrenceRule
      ? getCurrentRecurrenceDescription(currentTodo.recurrenceRule, t)
      : null

    // Show completed date if the todo is completed and has completedDate
    const completedDateStr =
      currentTodo.completedDate && currentTodo.status === TODO_STATUS.COMPLETED
        ? t('todo.detail.completedDate', {
            date: formatCompletedDate(currentTodo.completedDate, t),
          })
        : null

    return (
      <div className={styles.timelayout}>
        <span>{dueDateStr}</span>

        {rruleStr && (
          <div>
            <span>{rruleStr}</span>
          </div>
        )}

        {completedDateStr && (
          <div>
            <span>{completedDateStr}</span>
          </div>
        )}
      </div>
    )
  }

  const handleViewMore = () => {
    setShowNoteDetail(true)
  }

  useEffect(() => {
    const nRef = noteRef.current
    if (nRef) {
      const nRefHeight = nRef.clientHeight
      const nRefScrollHeight = nRef.scrollHeight
      if (nRefHeight < nRefScrollHeight) {
        setIsViewMore(true)
      }
    }
  }, [noteRef])

  const urlify = (text: string) => {
    if (!text) return null
    const urlRegex = /(https?:\/\/[^\s]+)/g
    return text.replace(urlRegex, (url) => `<a href="${url}" target="_blank">${url}</a>`)
  }

  const renderNote = () => {
    if (!currentTodo.description) return null

    const linkifyNote = urlify(currentTodo.description)

    return (
      <div className={styles.section}>
        <div className={styles.note}>
          <div className={styles.itemIconWrapper}>
            <img src={noteIcon} alt="note" className={styles.noteIcon} />
          </div>
          <div
            className={`${styles.textContent} ${isViewMore ? styles.viewMore : ''}`}
            ref={noteRef}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${linkifyNote}</p>`) }}
          />
        </div>
        {isViewMore && (
          <button className={styles.viewMoreButton} onClick={handleViewMore}>
            {t('calendar.detail.viewMore')}
          </button>
        )}
      </div>
    )
  }

  const renderReminders = () => {
    if (!currentTodo.alarms || currentTodo.alarms.length === 0) return null

    return (
      <div className={styles.section}>
        <div className={styles.reminders}>
          <div className={styles.itemIconWrapper}>
            <img src={reminderIcon} alt="Reminders" className={styles.reminderIcon} />
          </div>
          <div>
            {currentTodo.alarms.map((alarm: TaskReminder, index: number) => {
              const reminderTimeStr = getReminderTimeStr(
                alarm,
                currentTodo.dueDate,
                currentTodo.startDate,
                t,
              )
              return <span key={index}>{reminderTimeStr}</span>
            })}
          </div>
        </div>
      </div>
    )
  }
  const renderFolder = () => {
    const folder = getFolder()
    if (!folder) return null

    return (
      <div className={styles.section}>
        <div className={styles.folder}>
          <div className={styles.itemIconWrapper}>
            <SVG
              src={taskIcon}
              className={styles.folderIcon}
              style={{ color: folder?.calendarColor?._cdata || '#fdff45' } as React.CSSProperties}
            />
          </div>
          <div>
            <span>{folder.displayName || ''}</span>
            <br />
            <span>{folder?.userInfo?.email || folder?.owner || ''}</span>
          </div>
        </div>
      </div>
    )
  }

  const todoDetailRef = useRef<HTMLDivElement>(null)

  return (
    <div className={styles.todoDetail} ref={todoDetailRef}>
      {renderToolbar()}
      {renderTitle()}
      {renderTime()}
      {renderNote()}
      {renderReminders()}
      {renderFolder()}
      <Modal
        wrapClassName={classNames('noteDetailModal')}
        className={classNames('mobile-fullpage')}
        open={showNoteDetail}
        footer={null}
        onCancel={() => setShowNoteDetail(false)}
        width={500}
        destroyOnHidden
        closable={false}
        modalRender={(modal) => {
          const modalElement = modal as React.ReactElement<{ style?: React.CSSProperties }>
          return React.cloneElement(modalElement, {
            style: { ...modalElement.props.style, padding: 0, overflow: 'hidden' },
          })
        }}
      >
        <NoteDetail note={currentTodo.description} onClose={() => setShowNoteDetail(false)} />
      </Modal>
    </div>
  )
}

export default TodoDetails
